import { Box, Button, Card, CardContent, Grid, Modal, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";

import { AppDispatch, RootState } from "../../configureStore";
import TaskCard from "../molecules/TaskCard";
import TaskList from "../molecules/TaskList";
import { getReturns, RESET_RETURNS } from "../../redux/reducer/returns";
import { Task } from "../../interface/returns";
import { checkHealth } from "../../redux/reducer/returnOps";
import { useInFlightSummary } from "../../hooks/useInFlightSummary";
import { promptManagementList, configManagementList } from "../../utils";

interface TaxReturnsParams {}

const TaxReturns = ({}: TaxReturnsParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { accounts } = useMsal();

    const { activeCount: inFlightCount } = useInFlightSummary();
    const canAccessPromptManagement = accounts[0] && promptManagementList.includes(accounts[0].username.toLowerCase());
    const canAccessConfigManagement = accounts[0] && configManagementList.includes(accounts[0].username.toLowerCase());

    useEffect(() => {
        dispatch(getReturns({}));

        // Poll for new tasks every 5 seconds
        const pollInterval = setInterval(() => {
            dispatch(getReturns({}));
        }, 5000);

        return () => {
            clearInterval(pollInterval);
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(checkHealth());

        // Poll for new tasks every 5 seconds
        const pollInterval = setInterval(() => {
            dispatch(checkHealth());
        }, 10000);

        return () => {
            clearInterval(pollInterval);
        };
    }, [dispatch]);

    const [selectedTaskType, setSelectedTaskType] = useState<"identify" | "extract" | "grouping" | null>(null);

    const groupingTasksUnsorted = useSelector<RootState, Task[]>(({ returns }) => returns.groupTasks);
    const identifyTasksUnsorted = useSelector<RootState, Task[]>(({ returns }) => returns.identifyTasks);
    const extractTasksUnsorted = useSelector<RootState, Task[]>(({ returns }) => returns.extractTasks);
    const isDev = useSelector<RootState, boolean>(({ ui }) => ui.isDev);

    const groupingTasks = useMemo<Task[]>(
        () =>
            [...groupingTasksUnsorted].sort((a, b) =>
                a.status === "completed" ? 1 : b.status === "completed" ? -1 : 0,
            ),
        [groupingTasksUnsorted],
    );
    const identifyTasks = useMemo<Task[]>(
        () =>
            [...identifyTasksUnsorted].sort((a, b) =>
                a.status === "completed" ? 1 : b.status === "completed" ? -1 : 0,
            ),
        [identifyTasksUnsorted],
    );
    const extractTasks = useMemo<Task[]>(
        () =>
            [...extractTasksUnsorted].sort((a, b) =>
                a.status === "completed" ? 1 : b.status === "completed" ? -1 : 0,
            ),
        [extractTasksUnsorted],
    );

    const handleTaskClick = useCallback(
        (task: Task) => {
            const queryParam = task.key ? `?key=${task.key}` : "";
            navigate(`${selectedTaskType}/${task.id}${queryParam}`);
        },
        [navigate, selectedTaskType],
    );

    const handleCloseModal = () => {
        setSelectedTaskType(null);
    };

    useEffect(() => {
        dispatch({ type: RESET_RETURNS });
    }, []);

    return (
        <>
            <Typography
                variant="h4"
                sx={{ mb: 3, fontWeight: 600 }}
            >
                Tax Return Processing
            </Typography>

            <Grid
                container
                spacing={3}
            >
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box onClick={() => setSelectedTaskType("identify")}>
                        <TaskCard
                            title="Identify Pages"
                            description="Identify and classify tax return pages. Determine page types and document structure."
                            taskCount={identifyTasks.filter((t) => t.status !== "completed").length}
                            icon="FindInPageOutlined"
                            color="#9c27b0"
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box onClick={() => setSelectedTaskType("extract")}>
                        <TaskCard
                            title="Extract Data"
                            description="Extract and process data from identified tax return pages. OCR scanning and data validation."
                            taskCount={extractTasks.filter((t) => t.status !== "completed").length}
                            icon="DataExtractionOutlined"
                            color="#4caf50"
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box onClick={() => setSelectedTaskType("grouping")}>
                        <TaskCard
                            title="Match Forms"
                            description="Match tax return forms to individuals and businesses. Assign forms to the correct entities for processing."
                            taskCount={groupingTasks.filter((t) => t.status !== "completed").length}
                            icon="ReorderOutlined"
                            color="#2196f3"
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box onClick={() => navigate("/returns/inflight")}>
                        <TaskCard
                            title="In-Flight Messages"
                            description="Monitor messages currently being processed by Service Bus listeners"
                            taskCount={inFlightCount}
                            icon="Speed"
                            color="#00bcd4"
                        />
                    </Box>
                </Grid>
                {isDev && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card
                            sx={{
                                height: "100%",
                                cursor: "pointer",
                                transition: "transform 0.2s, box-shadow 0.2s",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: 4,
                                },
                            }}
                            onClick={() => navigate("/return-ops")}
                        >
                            <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "50%",
                                            backgroundColor: "#ff9800",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "white",
                                            mr: 2,
                                        }}
                                    >
                                        <Typography variant="h6">⚙️</Typography>
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Backend Management
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ flex: 1 }}
                                >
                                    Access backend operations and management tools for tax return processing.
                                </Typography>
                                <Button
                                    variant="text"
                                    color="primary"
                                    sx={{ mt: 2, justifyContent: "flex-start", p: 0 }}
                                >
                                    Go to Management →
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {canAccessPromptManagement && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box onClick={() => navigate("/prompt-management")}>
                            <TaskCard
                                title="Prompt Management"
                                description="Edit and test AI prompts for tax return processing"
                                taskCount={-1}
                                icon="Psychology"
                                color="#9c27b0"
                            />
                        </Box>
                    </Grid>
                )}
                {canAccessConfigManagement && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box onClick={() => navigate("/config-management")}>
                            <TaskCard
                                title="Config Management"
                                description="Manage tax return form configurations"
                                taskCount={-1}
                                icon="Tune"
                                color="#607d8b"
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>

            <Modal
                open={selectedTaskType !== null}
                onClose={handleCloseModal}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                        maxWidth: 600,
                        width: "90%",
                        maxHeight: "80vh",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    <TaskList
                        tasks={
                            selectedTaskType === "grouping"
                                ? groupingTasks
                                : selectedTaskType === "identify"
                                  ? identifyTasks
                                  : selectedTaskType === "extract"
                                    ? extractTasks
                                    : []
                        }
                        onTaskClick={handleTaskClick}
                        title={
                            selectedTaskType === "grouping"
                                ? "Match Forms Tasks"
                                : selectedTaskType === "identify"
                                  ? "Identify Pages Tasks"
                                  : selectedTaskType === "extract"
                                    ? "Extract Data Tasks"
                                    : ""
                        }
                    />
                </Box>
            </Modal>
        </>
    );
};

export default TaxReturns;
